/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, InstallerToken } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type RevokeInstallerTokenMutationConfig = {
  onSuccess?: (data: void, variables: InstallerToken) => void;
  onError?: (error: unknown, variables: InstallerToken) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: InstallerToken) => Promise<unknown> | void;
};

/**
 * Hook for revoking an installer token by ID through the management plane API.
 *
 * @param config.onSuccess - Called after successful installer token revocation
 * @param config.onError - Called if installer token revocation fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @example
 * ```tsx
 * const { mutate } = useRevokeInstallerTokenMutation({
 *   onSuccess: (data) => console.log('Installer token revoked:', data),
 *   onError: (error) => console.error('Installer token revocation failed:', error)
 * });
 *
 * // Revoke installer token
 * mutate({ tokenId: '9fb763f7-33de-4969-a7a0-b808f4637318', ... });
 * ```
 */

export function useRevokeInstallerTokenMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: RevokeInstallerTokenMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error, isPending } = useMutation<void, ApiError, InstallerToken>({
    mutationFn: async (installerToken: InstallerToken) => {
      await mgmtPlaneAPIClient.installerTokens.revokeInstallerToken({
        tenantId: tenantId!,
        tokenId: installerToken.tokenId,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error, isPending };
}
